import type { Persister } from '@tanstack/query-persist-client-core'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { createPersister } from '@/lib/cache/persister'
import {
  isTodayDashboardQuery,
  shouldPersistQuery,
} from '@/lib/cache/query-filter'
import { shouldDehydrateOfflineMutation } from '@/lib/offline/offline-queue'
import { initializeOfflineQueue } from '@/lib/offline/online-bridge'
import { queryClient } from '@/lib/queryClient'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import {
  persistQueryClientRestore,
  persistQueryClientSubscribe,
} from '@tanstack/query-persist-client-core'
import { IsRestoringProvider, QueryClientProvider } from '@tanstack/react-query'

/**
 * How old cached data can be before we skip restore entirely and just fetch fresh.
 * This prevents showing yesterday's data for 1-2 seconds on app open.
 * 5 minutes = if you closed the tab and reopened quickly, you get instant render.
 * Anything older = normal loading states, but with fresh data.
 */
const STALE_THRESHOLD = 1000 * 60 * 5 // 5 minutes

/**
 * Today-dashboard keys get a much longer restore window than everything else.
 * Today is the landing surface and every widget gates its own skeleton on its
 * own query, so a cold open past the 5-minute window is a full wall of shimmer.
 * Restoring the last-known dashboard paints instantly and revalidates silently.
 * Past 24h the snapshot is dropped outright — a day-old dashboard is a lie.
 */
const TODAY_DASHBOARD_MAX_AGE = 1000 * 60 * 60 * 24 // 24 hours

/**
 * Outer cap handed to persistQueryClientRestore. It has to cover the widest
 * window any key is allowed to use (the Today one), because it is applied to
 * the persisted blob as a whole; per-key freshness is enforced by the guard
 * below.
 */
const MAX_AGE = TODAY_DASHBOARD_MAX_AGE

/**
 * Wraps a real persister to skip restore if the cached data is too stale.
 * Still persists on every save (so fresh data is always written), but on restore
 * it checks the timestamp and drops what has aged out:
 * - past 5 minutes, only Today-dashboard keys survive
 * - past 24 hours, nothing survives
 */
function createFreshnessGuard(inner: Persister): Persister {
  return {
    persistClient: inner.persistClient,
    removeClient: inner.removeClient,
    restoreClient: async () => {
      const cached = await inner.restoreClient()
      if (!cached) return undefined

      // Paused writes are never subject to the read-cache freshness policy.
      // Those windows exist so nobody is shown yesterday's numbers as if they
      // were today's — a reason that has nothing to do with a change the user
      // made and is still waiting on. Dropping the blob wholesale, as this
      // guard used to, would silently discard queued work; the queue enforces
      // its own, much longer, expiry (OFFLINE_QUEUE_MAX_AGE_MS).
      const mutations = cached.clientState.mutations ?? []

      const age = Date.now() - cached.timestamp
      if (age > TODAY_DASHBOARD_MAX_AGE) {
        console.info(
          `[QueryCache] Skipping restore — cache is ${Math.round(age / 1000)}s old (max: ${TODAY_DASHBOARD_MAX_AGE / 1000}s)`,
        )
        return mutations.length > 0
          ? { ...cached, clientState: { ...cached.clientState, queries: [] } }
          : undefined
      }

      const isPastGeneralWindow = age > STALE_THRESHOLD
      const queries = cached.clientState.queries.filter(query => {
        if (!shouldPersistQuery(query.queryKey)) return false
        // Everything but the Today dashboard goes back to normal loading
        // states once the general freshness window has passed.
        return !isPastGeneralWindow || isTodayDashboardQuery(query.queryKey)
      })

      if (queries.length === 0 && mutations.length === 0) return undefined

      console.info(
        `[QueryCache] Restoring ${queries.length} ${isPastGeneralWindow ? 'Today-dashboard ' : ''}queries and ${mutations.length} pending writes — cache is ${Math.round(age / 1000)}s old`,
      )
      return {
        ...cached,
        clientState: {
          ...cached.clientState,
          queries,
        },
      }
    },
  }
}

// Initialised at module scope, not in an effect. `hydrate()` resolves a
// restored mutation's options — including its `mutationFn` — the moment it
// rebuilds it from storage, and restore begins in this provider's first effect.
// Defaults registered later would arrive after that, and every rehydrated write
// would come back inert.
initializeOfflineQueue(queryClient)

export function QueryCacheProvider({ children }: { children: ReactNode }) {
  const userId = useAuth(s => s.user?.id)
  const orgId = useOrganizationStore(s => s.currentOrganization?.id)
  const [isRestoring, setIsRestoring] = useState(false)

  // Scope the cache per user AND org: query keys are org-agnostic (the org is
  // a request header), so an org-agnostic cache restored after an org switch
  // would serve the previous org's rows under identical query keys.
  const scopeKey = userId && orgId ? `${userId}:${orgId}` : undefined
  const prevScopeKeyRef = useRef<string | undefined>(undefined)
  // Held so sign-out can wipe the stored snapshot, not just stop writing to it.
  const persisterRef = useRef<Persister | null>(null)

  useEffect(() => {
    const prevScopeKey = prevScopeKeyRef.current
    prevScopeKeyRef.current = scopeKey

    // In-session user/org switch: the in-memory cache still holds the previous
    // scope's rows under org-agnostic query keys. Drop it all before restoring
    // or subscribing, otherwise those rows both render in the new org and get
    // persisted into the new org's storage bucket (which would then serve
    // cross-org data on the next boot). The org-switch removeQueries list only
    // covers known keys — this catches everything.
    if (prevScopeKey && scopeKey && prevScopeKey !== scopeKey) {
      void queryClient.cancelQueries()
      queryClient.removeQueries()
    }

    if (!scopeKey) {
      // Sign-out (AuthProvider.handleLogout clears the user, then the query
      // cache). Stopping the subscription isn't enough — the last-known
      // dashboard is still sitting in IndexedDB/SQLite, and the next person to
      // sign in on this machine must never see it. Wipe it outright.
      const orphaned = persisterRef.current
      persisterRef.current = null
      if (prevScopeKey && orphaned) {
        void Promise.resolve(orphaned.removeClient()).catch(
          (error: unknown) => {
            console.warn(
              '[QueryCache] Failed to clear persisted cache on sign-out',
              error,
            )
          },
        )
      }
      setIsRestoring(false)
      return
    }

    let cancelled = false
    let unsubscribe: (() => void) | undefined
    setIsRestoring(true)

    const initializePersistence = async () => {
      const createdPersister = await createPersister(scopeKey)
      if (cancelled || !createdPersister) return
      persisterRef.current = createdPersister

      const options = {
        queryClient,
        persister: createFreshnessGuard(createdPersister),
        maxAge: MAX_AGE,
        buster: '',
        dehydrateOptions: {
          shouldDehydrateQuery: (query: {
            state: { status: string }
            queryKey: readonly unknown[]
          }) =>
            query.state.status === 'success' &&
            shouldPersistQuery(query.queryKey),
          /**
           * Paused writes ride along with the read cache so a change made on a
           * dead connection survives a reload or a closed lid. The predicate is
           * deliberately narrow — paused AND carrying an `['offline', …]`
           * mutation key — which is what keeps financial and destructive
           * writes out of the queue by construction rather than by anyone
           * remembering to exclude them.
           *
           * A persisted mutation is inert without a matching
           * `setMutationDefaults`; those are registered by
           * `initializeOfflineQueue` before restore runs, below.
           */
          shouldDehydrateMutation: shouldDehydrateOfflineMutation,
        },
      }

      await persistQueryClientRestore(options)
      if (cancelled) return

      unsubscribe = persistQueryClientSubscribe(options)
      // Background-refresh stale restored data. Subscribing first also ensures
      // the current cache is saved even when auth/org resolved after mount.
      await queryClient.invalidateQueries({ stale: true })
    }

    void initializePersistence()
      .catch((error: unknown) => {
        console.warn('[QueryCache] Failed to initialize persistence', error)
      })
      .finally(() => {
        if (!cancelled) setIsRestoring(false)
      })

    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [scopeKey])

  return (
    <QueryClientProvider client={queryClient}>
      <IsRestoringProvider value={isRestoring}>{children}</IsRestoringProvider>
    </QueryClientProvider>
  )
}
